// -------------------------------------------------
// Flywire INSTANT return leg for the BookingPal reserve flow.
// The Flywire checkout redirects here (returnUrl = /request-to-book-flywire).
// Mirrors VT-FE PropertyReserve's return-leg useEffect: status-gated,
// idempotent, never trusts component state (fresh mount). On a referenced
// success it (a) books the BP CHANNEL on the hub via POST /api/booking/reserve
// (payment-gated, partner sessions allowed; Util/partnerReserve.js) and (b) creates
// the reservation-of-record (VT-BE for VT, SHub for SH — via RESERVATION_API).
// Byte-identical across the VT and SH extranets; per-repo values via constants.
// -------------------------------------------------
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import swal from "sweetalert";
import AuthService from "../../services/auth.service";
import { buildReservePayload, classifyReserveResponse, PAYMENT_RETRY_DELAYS_MS } from "../../Util/partnerReserve";

const OPS_EMAIL = "reservations@villatracker.com";

const RequestToBookFlywire = () => {
  const history = useHistory();
  const [message, setMessage] = useState("Finalizing your booking…");

  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const confirmation = params.get("confirmation");
      if (!confirmation) {
        setMessage("Nothing to process.");
        return;
      }

      const reference = params.get("reference");
      const status = params.get("status");
      const amount = params.get("amount");
      const payment_method = params.get("payment_method");
      const sig = params.get("sig");

      // A reservation is only created for an explicit, referenced success.
      if (status !== "success" || !reference) {
        if (status && status !== "success") {
          setMessage("Payment was not completed — no reservation was created and your card was not charged.");
          swal("Payment not completed", "No reservation was created and your card was not charged.", "error");
        } else {
          setMessage("Payment status unclear — please contact support.");
          swal(
            "Payment status unclear",
            `We could not confirm the payment result (confirmation ${confirmation}). Please contact ${OPS_EMAIL} before trying again — do NOT pay twice.`,
            "warning"
          );
        }
        return;
      }

      // Idempotency — set BEFORE any POST (guards React StrictMode double-invoke
      // and browser refreshes of the return page).
      const submittedKey = `flywireSubmitted_${confirmation}`;
      const guard = localStorage.getItem(submittedKey);
      if (guard === "done") {
        history.push("/reservations");
        return;
      }
      if (guard === "inflight") return;
      localStorage.setItem(submittedKey, "inflight");

      let pending = null;
      try {
        pending = JSON.parse(localStorage.getItem("bpPendingReservation"));
      } catch (e) { /* corrupted — handled as missing below */ }

      if (!pending) {
        // Reset the guard so a refresh re-shows this message instead of
        // silently short-circuiting on a stuck 'inflight'.
        localStorage.removeItem(submittedKey);
        setMessage("Payment received — booking needs attention.");
        swal(
          "Payment received — booking needs attention",
          `We received your payment confirmation (reference ${reference || confirmation}) but couldn't complete the booking in this browser. Please contact ${OPS_EMAIL} and do NOT pay again.`,
          "warning"
        );
        return;
      }

      const ref = reference || confirmation;
      try {
        // (a) Book the CHANNEL on the hub via POST /api/booking/reserve (partner
        //     sessions are refused on /create). Payment-gated: the hub needs the
        //     VERIFIED Flywire callback for payment.ref, and books once per ref (a
        //     replay answers 200 duplicate). Skip if a prior attempt already booked
        //     it (addReservation failed after → we retry only step b).
        let confirmationId = pending.bpConfirmationId || "";
        let confirmationCode = pending.bpConfirmationCode || "";
        if (!pending.bpDone) {
          const payload = buildReservePayload(pending, { reference, confirmation });
          let verdict = null;
          for (let attempt = 0; ; attempt++) {
            let status = 0;
            let body = null;
            try {
              const resp = await AuthService.bookingReserve(payload);
              status = resp?.status || 0;
              body = resp?.data;
            } catch (e) {
              status = e?.response?.status || 0;
              body = e?.response?.data;
            }
            verdict = classifyReserveResponse({ status, body });
            // 402: the verified webhook can lag the redirect — wait and retry.
            if (verdict.outcome !== "payment_pending" || attempt >= PAYMENT_RETRY_DELAYS_MS.length) break;
            setMessage("Waiting for the payment to be verified…");
            await new Promise((r) => setTimeout(r, PAYMENT_RETRY_DELAYS_MS[attempt]));
          }

          if (verdict.outcome !== "confirmed") {
            // Not booked (yet). Free the guard so a refresh can ask again (safe:
            // the hub books at most once per payment.ref). Keep the pending booking.
            localStorage.removeItem(submittedKey);
            const why = [verdict.code, verdict.message].filter(Boolean).join(": ");
            if (verdict.outcome === "payment_pending") {
              setMessage("Your payment is still being verified — we'll confirm the booking by email.");
              swal(
                "Payment still being verified",
                `Your payment (reference ${ref}) is still being verified. We'll confirm the booking by email — do NOT pay again. Questions: ${OPS_EMAIL}.`,
                "info"
              );
            } else if (verdict.outcome === "rejected") {
              setMessage("The booking could not be made — our staff will refund or rebook.");
              swal(
                "Booking not made",
                `The booking could not be completed (${why || "refused"}). Your payment reference is ${ref}: our staff will refund it or rebook with you. Contact ${OPS_EMAIL} — do NOT pay again.`,
                "error"
              );
            } else {
              setMessage("We're confirming your booking — please do not rebook.");
              swal(
                "Confirming your booking",
                `We could not confirm the booking yet${why ? ` (${why})` : ""}. It may already be made — do NOT rebook or pay again. We're confirming and will email you. Payment reference: ${ref}.`,
                "warning"
              );
            }
            return;
          }
          confirmationId = verdict.reservationId;
          confirmationCode = "";
          // Persist so a retry (after an addReservation failure) does NOT
          // re-book the channel — go straight to step (b).
          pending.bpDone = true;
          pending.bpConfirmationId = confirmationId;
          pending.bpConfirmationCode = confirmationCode;
          localStorage.setItem("bpPendingReservation", JSON.stringify(pending));
        }

        // (b) Create the reservation-of-record (RESERVATION_API), carrying the
        //     Flywire payment fields + the BP channel handles. flywireAmount
        //     stays the authoritative amount we asked Flywire to charge (set on
        //     the stored vtbe payload) — NOT the return-URL echo.
        const reservationPayload = {
          ...pending.vtbe,
          payment_type: "instant",
          flywireCallbackId: confirmation,
          flywireReference: reference,
          flywireStatus: status,
          flywireReturnAmount: amount,      // Flywire's return-URL echo (audit)
          flywirePaymentMethod: payment_method,
          flywireSig: sig,
          flywirePaymentStatus: "charged_pending_webhook",
          bpConfirmationId: confirmationId,
          bpConfirmationCode: confirmationCode,
        };
        const res = await AuthService.addReservation(reservationPayload);
        if (res?.status === 200 || res?.data) {
          localStorage.setItem(submittedKey, "done");
          localStorage.removeItem("bpPendingReservation");
          setMessage("Reservation completed. Redirecting…");
          history.push("/reservations");
        } else {
          throw new Error("add-reservation did not return success");
        }
      } catch (err) {
        console.error("Flywire return: booking completion failed", err);
        localStorage.removeItem(submittedKey); // allow a refresh to retry
        setMessage("We couldn't save your reservation — please contact support before paying again.");
        swal(
          "Reservation not saved",
          `Your payment reference is ${reference || confirmation}. Please contact ${OPS_EMAIL} before paying again — the card may already be charged.`,
          "error"
        );
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <h3>{message}</h3>
    </div>
  );
};

export default RequestToBookFlywire;
